import { ColorModel } from "#lib/SiteSettingsColorModel.svelte.js";
import { untrack } from "svelte";
import { FONT_VARIATION_PROPERTIES, FONT_FEATURE_PROPERTIES, is_stylistic_set } from "#lib/font_properties.js";
import { generate_font_faces as generate_font_faces_util, is_phone, is_url, is_strict_integer } from "#lib/util.js";
import t from "#lib/i18n.svelte.js";
import { normalize_scale } from "#lib/typography_scale.js";
import { font_family_stack } from "#lib/font_fallback.js";

export class BaseNavItemModel {
  label = $state('');
  image_ref = $state('');
  show_label_with_image = $state(false);
  image = $state(''); // resolves to a full image entry
  size = $state(20);  
  surface = $state('ghostly');

  constructor({ label = '', image_ref = '', show_label_with_image = false, image = '', size = 20, surface = 'ghostly' }) {
    this.label = label;
    this.image_ref = image_ref;
    this.show_label_with_image = show_label_with_image;
    this.image = image;
    this.size = size;
    this.surface = surface;
  }

  to_json() {
    return {
      type: this.type,
      label: this.label,
      image_ref: this.image_ref,
      show_label_with_image: this.show_label_with_image,
      size: this.size,
      surface: this.surface
    };
  }
}

export class EntryNavItemModel extends BaseNavItemModel {
  entry_preview_ref = $state('');
  entry_preview = $state(''); // resolves to an entry_preview

  constructor({
    entry_preview_ref, 
    image_ref, 
    label, 
    show_label_with_image, 
    entry_preview, 
    image,
    is_home_page,
    size,
    surface
  }) {
    super({ label, image_ref, show_label_with_image, image, size, surface });
    this.is_home_page = is_home_page;
    this.type = 'entry';
    this.entry_preview_ref = entry_preview_ref;
    this.entry_preview = entry_preview;
  }

  get href() {
    if (this.is_home_page) return '/';
    return this.entry_preview?.slug 
      ?`/${this.entry_preview?.slug}-${this.entry_preview_ref}`
      : `/${this.entry_preview_ref}`;
  }

  to_json() {
    return {
      ...super.to_json(),
      entry_preview_ref: this.entry_preview_ref,
      surface: this.surface
    };
  }
}

export class SpacerNavItemModel {
  constructor() { this.type = 'spacer'; }
  to_json() { return { type: 'spacer' } }
};

export class SeparatorNavItemModel {
  constructor() { this.type = 'separator'; }
  to_json() { return { type: 'separator' } }
};

export class MobileMenuBreakpointNavItemModel {
  constructor() { this.type = 'mobile_menu_breakpoint'; }
  to_json() { return { type: 'mobile_menu_breakpoint' } }
};

export class PhoneNavItemModel extends BaseNavItemModel {
  phone = $state('');
  constructor({ phone, label, image_ref, image, show_label_with_image, size, surface }) {
    super({ label, image_ref, show_label_with_image, image, size, surface });
    this.type = 'phone'; 
    this.phone = phone;
  }
  get href() {
    if(this.phone) {
      return `tel:${this.phone.replace(/[^0-9+]|\+\d+\(0\)/g, m => m.startsWith('+') ? m.replace('(0)', '') : '')}`;
    }
    return '';
  }
  to_json() { return { ...super.to_json(), phone: this.phone }; }
}

export class EmailNavItemModel extends BaseNavItemModel {
  email = $state('');
  constructor({ email, label, image_ref, image, show_label_with_image, size, surface }) {
    super({ label, image_ref, show_label_with_image, image, size, surface });
    this.type = 'email'; 
    this.email = email;
  }
  get href() {
    if(this.email) {
      return `mailto:${this.email}`;
    }
    return '';
  }
  to_json() { return { ...super.to_json(), email: this.email }; }
}

export class WeblinkNavItemModel extends BaseNavItemModel {
  href = $state('');
  constructor({ href, label = 'Untitled', image_ref, image, show_label_with_image, size, surface }) {
    super({ label, image_ref, show_label_with_image, image, size, surface });
    this.type = 'weblink';
    this.href = href;
  }
  to_json() { return { ...super.to_json(), href: this.href }; }
}

export class UserProfileNavItemModel extends BaseNavItemModel {
  constructor({ label, show_label_with_image, image_ref, image, size, surface }) {
    super({ label, image_ref, show_label_with_image, image, size, surface });
    this.type = 'user_profile';
  }
  get href() {
    return `/login`;
  }
  to_json() { return { ...super.to_json() }; }
}

export class MessagesNavItemModel extends BaseNavItemModel {
  message_text = $state('');
  constructor({ label, show_label_with_image, image_ref, image, size, surface, message_text = '' }) {
    super({ label, image_ref, show_label_with_image, image, size, surface });
    this.type = 'messages';
    this.message_text = message_text;
  }
  get href() {
    let base_href = '/messages';
    if (this.message_text && this.message_text.trim() !== '') {
      base_href += `?text=${encodeURIComponent(this.message_text.trim())}`;
    }
    return base_href;
  }
  to_json() { 
    return { 
      ...super.to_json(), 
      message_text: this.message_text
    }; 
  }
}

export class CalendarNavItemModel extends BaseNavItemModel {
  constructor({ label, show_label_with_image, image_ref, image, size, surface }) {
    super({ label, image_ref, show_label_with_image, image, size, surface });
    this.type = 'calendar';
  }
  get href() {
    return `/calendar`;
  }
  to_json() { return { ...super.to_json() }; }
}

export class PostersNavItemModel extends BaseNavItemModel {
  constructor({ label, show_label_with_image, image_ref, image, size, surface }) {
    super({ label, image_ref, show_label_with_image, image, size, surface });
    this.type = 'posters';
  }
  get href() {
    return `/posters`;
  }
  to_json() { return { ...super.to_json() }; }
}

/**
 * Custom Form Field Model for user signup forms
 */
export class CustomFormFieldModel {
  id = $state('');
  label = $state('');
  placeholder = $state('');
  type = $state('text'); // text, longtext, float_number, integer_number, checkbox, select, phone, weblink
  required = $state(false);
  options = $state([]); // For select type fields

  constructor({
    id = '',
    label = '',
    placeholder = '',
    type = 'text',
    required = false,
    options = []
  }) {
    this.id = id;
    this.label = label;
    this.placeholder = placeholder;
    this.type = type;
    this.required = required;
    this.options = Array.isArray(options) ? options : [];
  }

  /**
   * Validates a value for this field (client side)
   * @param {any} value - The value to validate
   * @returns {{ valid: boolean, error?: string }}
   */
  validate_value(value) {
    // Basic required validation
    if (this.required) {
      if (this.type === 'checkbox') {
        if (value !== true) {
          return { valid: false, error: t('field_is_required', { field: this.label || this.id }) };
        }
      } else if (value === null || value === undefined || value.toString().trim() === '') {
        return { valid: false, error: t('field_is_required', { field: this.label || this.id }) };
      }
    }

    // If not required and empty, it's valid
    if (!this.required && (value === null || value === undefined || value.toString().trim() === '')) {
      return { valid: true };
    }

    // Simple type-specific validation
    if (value !== null && value !== undefined && value.toString().trim() !== '') {
      const str_value = value.toString().trim();

      switch (this.type) {
        case 'float_number':
          if (isNaN(Number(str_value))) {
            return { valid: false, error: t('field_must_be_valid_number', { field: this.label || this.id }) };
          }
          break;
        
        case 'integer_number':
          if (!is_strict_integer(str_value)) {
            return { valid: false, error: t('field_must_be_valid_integer', { field: this.label || this.id }) };
          }
          break;

        case 'phone':
          if (!is_phone(str_value)) {
            return { valid: false, error: t('field_must_be_valid_phone_number', { field: this.label || this.id }) };
          }
          break;

        case 'weblink':
          if (!is_url(str_value)) {
            return { valid: false, error: t('field_must_be_valid_url', { field: this.label || this.id }) };
          }
          break;
      }
    }

    return { valid: true };
  }

  to_json() {
    return {
      id: this.id,
      label: this.label,
      placeholder: this.placeholder,
      type: this.type,
      required: this.required,
      options: this.options
    };
  }
}



// NOTE: We could potentially use the ContainerModel for nav_items in the future
// Currently there are a few differences.
export default class SiteSettingsModel {
  name = $state('');
  description = $state('');
  legal_name = $state('');
  support_email = $state('');
  messages_title = $state('');
  messages_intro = $state('');
  event_reminder_emails_enabled = $state(true); // global toggle for scheduled event reminder emails
  announcement = $state(''); // text displayed in the announcement banner above the dynamic island
  announcement_enabled = $state(true); // whether to show the announcement
  
  
  // Logo (used for Desktop Menu)
  logo_ref = $state('');
  logo = $state(''); // derived from logo_ref
  // App icon (used for Mobile Menu in Admin Mode and Favicon). Must be a square image.
  app_icon_ref = $state('');
  app_icon = $state(''); // derived from app_icon_ref
  // Email signature settings
  email_signature_image_ref = $state('');
  email_signature_image = $state(''); // derived from email_signature_image_ref
  email_signature_text = $state('');
  // Home page preview (used to determine the home page)
  home_page_preview_ref = $state('');
  home_page_preview = $state(''); // derived from home_page_preview_ref
  // Terms and conditions page preview
  terms_and_conditions_preview_ref = $state('');
  terms_and_conditions_preview = $state(''); // derived from terms_and_conditions_preview_ref
  // Privacy policy page preview
  privacy_policy_preview_ref = $state('');
  privacy_policy_preview = $state(''); // derived from privacy_policy_preview_ref
  
  grant_member_access_to_new_users = $state(false);
  signup_data_processing_consent_required = $state(false);
  nav_items = $state([]); // top-level menu
  aspect_ratios = $state([]);

  image_zoom = $state(true);
  
  menu_placement = $state();
  menu_track_start = $state();
  menu_track_end = $state();
  menu_background = $state();
  menu_elevation = $state();
  menu_mobile_alignment = $state();
  control_surface_align = $state('');
  primary_control_surface = $state(''); // Standalone (block) elements
  secondary_control_surface = $state(''); // Standalone (block) elements

  signup_or_login_text = $state('');
  min_access_role_for_all_module = $state(); // search & all
  min_access_role_for_user_profiles = $state(); // access to other users profile pages and e.g. their collections
  default_member_created_collection_entry_visibility = $state(); // visibility of collections created by members
  allow_member_collection_images = $state(false); // members may upload pictures into their own collections
  footer_color = $state();
  footer_visible = $state();
  footer_layout = $state();
  footer_outro = $state('');
  footer_image_ref = $state('');
  footer_image = $state(); // derived from footer_image_ref

  footer_nav_1_title = $state('');
  footer_nav_1_description = $state('');
  footer_nav_1_show_in_mobile_drawer = $state(false)
  footer_nav_1_items = $state([]);
  footer_nav_2_title = $state('');
  footer_nav_2_description = $state('');
  footer_nav_2_show_in_mobile_drawer = $state(false)
  footer_nav_2_items = $state([]);
  footer_nav_3_title = $state('');
  footer_nav_3_description = $state('');
  footer_nav_3_show_in_mobile_drawer = $state(false)
  footer_nav_3_items = $state([]);
  footer_nav_4_title = $state('');
  footer_nav_4_description = $state('');
  footer_nav_4_show_in_mobile_drawer = $state(false)
  footer_nav_4_items = $state([]);
  footer_nav_5_title = $state('');
  footer_nav_5_description = $state('');
  footer_nav_5_show_in_mobile_drawer = $state(false)
  footer_nav_5_items = $state([]);

  custom_css = $state(''); // custom css for indivual styles that do not fit into the system
  custom_form_fields = $state([]); // dynamic user signup form fields

  // Webhook configuration
  webhook_enabled = $state(false);
  webhook_url = $state('');
  webhook_events = $state([]); // array of event types like ['user.signup', 'user.signin', 'user.update']

  constructor(site_settings_raw) {
    // Since we have a quite deep object, let's make a deep clone as a safeguard.
    const site_settings_cloned = JSON.parse(JSON.stringify(site_settings_raw)); // structuredClone(site_settings_raw);
    // NOTE: We assume a correct site_settings_raw (which may use a default value) so
    // we don't use any default values down the class/object hierarchy.
    const {
      name, 
      description, 
      legal_name,
      support_email,
      messages_title,
      messages_intro,
      event_reminder_emails_enabled,
      announcement,
      announcement_enabled,
      logo_ref,
      logo,
      app_icon_ref,
      app_icon,
      email_signature_image_ref,
      email_signature_image,
      email_signature_text,
      home_page_preview_ref,
      home_page_preview,
      terms_and_conditions_preview_ref,
      terms_and_conditions_preview,
      privacy_policy_preview_ref,
      privacy_policy_preview,
      grant_member_access_to_new_users,
      signup_data_processing_consent_required,
      nav_items,
      menu_placement,
      menu_track_start,
      menu_track_end,
      menu_background,
      menu_elevation,
      menu_mobile_alignment,
      control_surface_align,
      primary_control_surface,
      secondary_control_surface,
      signup_or_login_text,
      min_access_role_for_all_module,
      min_access_role_for_user_profiles,
      default_member_created_collection_entry_visibility,
      allow_member_collection_images,
      colors,
      typography,
      aspect_ratios,
      image_zoom,
      footer_color,
      footer_visible,
      footer_layout,
      footer_outro,
      footer_image_ref,
      footer_image,
      footer_nav_1_title,
      footer_nav_1_description,
      footer_nav_1_show_in_mobile_drawer,
      footer_nav_1_items,
      footer_nav_2_title,
      footer_nav_2_description,
      footer_nav_2_show_in_mobile_drawer,
      footer_nav_2_items,
      footer_nav_3_title,
      footer_nav_3_description,
      footer_nav_3_show_in_mobile_drawer,
      footer_nav_3_items,
      footer_nav_4_title,
      footer_nav_4_description,
      footer_nav_4_show_in_mobile_drawer,
      footer_nav_4_items,
      footer_nav_5_title,
      footer_nav_5_description,
      footer_nav_5_show_in_mobile_drawer,
      footer_nav_5_items,
      custom_css,
      custom_form_fields,
      webhook_enabled,
      webhook_url,
      webhook_events,
    } = site_settings_cloned;

    this.name = name;
    this.description = description;
    this.legal_name = legal_name;
    this.support_email = support_email;
    this.messages_title = messages_title;
    this.messages_intro = messages_intro;
    this.event_reminder_emails_enabled = event_reminder_emails_enabled ?? true;
    this.announcement = announcement ?? '';
    this.announcement_enabled = announcement_enabled ?? true;
    
    this.logo_ref = logo_ref;
    this.app_icon_ref = app_icon_ref;
    this.email_signature_image_ref = email_signature_image_ref;
    this.email_signature_text = email_signature_text;
    this.home_page_preview_ref = home_page_preview_ref;
    this.terms_and_conditions_preview_ref = terms_and_conditions_preview_ref;
    this.privacy_policy_preview_ref = privacy_policy_preview_ref;
    this.grant_member_access_to_new_users = grant_member_access_to_new_users;
    this.signup_data_processing_consent_required = signup_data_processing_consent_required ?? false;
    this.menu_placement = menu_placement ?? 'docked';
    this.menu_track_start = menu_track_start ?? 6;
    this.menu_track_end = menu_track_end ?? 46;
    this.menu_background = menu_background;
    this.menu_elevation = menu_elevation;
    this.menu_mobile_alignment = menu_mobile_alignment ?? 'start';

    this.control_surface_align = control_surface_align ?? 'center';
    this.primary_control_surface = primary_control_surface ?? 'filled';
    this.secondary_control_surface = secondary_control_surface ?? 'outlined';

    this.signup_or_login_text = signup_or_login_text;
    this.min_access_role_for_all_module = min_access_role_for_all_module ?? 'public';
    this.min_access_role_for_user_profiles = min_access_role_for_user_profiles ?? 'admin';
    this.default_member_created_collection_entry_visibility = default_member_created_collection_entry_visibility ?? 'member';
    this.allow_member_collection_images = allow_member_collection_images === true;
    this.colors = new ColorModel(colors);

    this.typography = new TypographyModel(typography);
    this.aspect_ratios = aspect_ratios || ['3/1','8/3','2/1','16/9','3/2','4/3','1/1','3/4','2/3'];

    this.image_zoom = image_zoom ?? true;

    this.footer_color = footer_color;
    this.footer_visible = footer_visible;
    this.footer_layout = footer_layout;
    this.footer_outro = footer_outro;
    this.footer_image_ref = footer_image_ref;

    // TODO Michael: Once we build an UI for this we need an effect,
    // that fetches the logo when footer_image_ref changes.
    this.footer_image = footer_image;
    this.logo = logo;
    this.app_icon = app_icon;
    this.email_signature_image = email_signature_image;
    this.home_page_preview = home_page_preview;
    this.terms_and_conditions_preview = terms_and_conditions_preview;
    this.privacy_policy_preview = privacy_policy_preview;

    this.footer_nav_1_title = footer_nav_1_title;
    this.footer_nav_1_description = footer_nav_1_description;
    this.footer_nav_1_show_in_mobile_drawer = footer_nav_1_show_in_mobile_drawer;
    this.footer_nav_2_title = footer_nav_2_title;
    this.footer_nav_2_description = footer_nav_2_description;
    this.footer_nav_2_show_in_mobile_drawer = footer_nav_2_show_in_mobile_drawer;
    this.footer_nav_3_title = footer_nav_3_title;
    this.footer_nav_3_description = footer_nav_3_description;
    this.footer_nav_3_show_in_mobile_drawer = footer_nav_3_show_in_mobile_drawer;
    this.footer_nav_4_title = footer_nav_4_title;
    this.footer_nav_4_description = footer_nav_4_description;
    this.footer_nav_4_show_in_mobile_drawer = footer_nav_4_show_in_mobile_drawer;
    this.footer_nav_5_title = footer_nav_5_title;
    this.footer_nav_5_description = footer_nav_5_description;
    this.footer_nav_5_show_in_mobile_drawer = footer_nav_5_show_in_mobile_drawer;

    this.custom_css = custom_css;

    // Initialize webhook configuration
    this.webhook_enabled = webhook_enabled ?? false;
    this.webhook_url = webhook_url ?? '';
    this.webhook_events = webhook_events ?? [];

    // Initialize custom form fields
    untrack(() => {
      for (const field of custom_form_fields || []) {
        this.add_custom_form_field(field);
      }
    });

    untrack(() => {
      for (const nav_item of nav_items || []) {
        this.add_nav_item(nav_item, 'nav_items');
      }
      for (const nav_item of footer_nav_1_items || []) {
        this.add_nav_item(nav_item, 'footer_nav_1_items');
      }
      for (const nav_item of footer_nav_2_items || []) {
        this.add_nav_item(nav_item, 'footer_nav_2_items');
      }
      for (const nav_item of footer_nav_3_items || []) {
        this.add_nav_item(nav_item, 'footer_nav_3_items');
      }
      for (const nav_item of footer_nav_4_items || []) {
        this.add_nav_item(nav_item, 'footer_nav_4_items');
      }
      for (const nav_item of footer_nav_5_items || []) {
        this.add_nav_item(nav_item, 'footer_nav_5_items');
      }
    });

    // In case untrack() doesn't work, we need to use more verbose code like below:
    // 
    // for (const nav_item of nav_items) {
    //   // NOTE: We keep this code redundant with add_nav_item, so the svelte
    //   // compiler can do static analysis of the constructor
    //   const NavItemModel = this.__getNavItemModel(nav_item.type);
    //   this.nav_items.push(new NavItemModel(nav_item));
    // }
  }

  move_item(from_index, to_index, property = 'nav_items') {
    if (to_index >= this[property].length) {
      let k = to_index - this[property].length + 1;
      while (k--) {
        this[property].push(undefined);
      }
    }
    this[property].splice(
      to_index,
      0,
      this[property].splice(from_index, 1)[0]
    );
  }

  delete_item(index, property = 'nav_items') {
    this[property].splice(index, 1);
  }

  // NOTE: We trust that for type: 'entry' we get an already resolved preview object
  // For example: { type: 'entry', entry_id: 8, title: "About", slug: "about"}
  add_nav_item(nav_item, property = 'nav_items') {
    const NavItemModel = this.__getNavItemModel(nav_item.type);
    this[property].push(new NavItemModel(nav_item));
  }

  update_nav_item(index, updated_item, property = 'nav_items') {
    const NavItemModel = this.__getNavItemModel(updated_item.type);
    this[property][index] = new NavItemModel(updated_item);
  }

  /**
   * Adds an image reference to the specified item.
   * @param {number} index - The index of the item in the navigation array.
   * @param {string} image_ref - The image reference ID to add.
   * @param {string} [property='nav_items'] - The property of the navigation array (e.g., 'footer_nav_1_items').
   * @throws {Error} Throws an error if the index is out of bounds.
   */
  add_image_to_navitem(index, image_ref, property = 'nav_items') {
    if (index < 0 || index >= this[property].length) {
      throw new Error('Index out of bounds');
    }
    this[property][index].image_ref = image_ref;
    // this[property][index].image = ''; // Reset image, it will be resolved via effect
  }

  /**
   * Removes the image reference from the specified item.
   * @param {number} index - The index of the item in the navigation array.
   * @param {string} [property='nav_items'] - The property of the navigation array (e.g., 'footer_nav_1_items').
   * @throws {Error} Throws an error if the index is out of bounds.
   */
  remove_image_of_item(index, property = 'nav_items') {
    if (index < 0 || index >= this[property].length) {
      throw new Error('Index out of bounds');
    }
    this[property][index].image_ref = '';
    this[property][index].image = '';
  }

  /**
   * Adds a custom form field to the configuration
   * @param {Object} field_config - The field configuration object
   */
  add_custom_form_field(field_config) {
    const field = new CustomFormFieldModel(field_config);
    this.custom_form_fields.push(field);
  }

  /**
   * Updates a custom form field at the specified index
   * @param {number} index - The index of the field to update
   * @param {Object} field_config - The updated field configuration
   */
  update_custom_form_field(index, field_config) {
    if (index < 0 || index >= this.custom_form_fields.length) {
      throw new Error('Index out of bounds');
    }
    this.custom_form_fields[index] = new CustomFormFieldModel(field_config);
  }

  /**
   * Removes a custom form field at the specified index
   * @param {number} index - The index of the field to remove
   */
  remove_custom_form_field(index) {
    if (index < 0 || index >= this.custom_form_fields.length) {
      throw new Error('Index out of bounds');
    }
    this.custom_form_fields.splice(index, 1);
  }

  /**
   * Moves a custom form field from one position to another
   * @param {number} from_index - The current index of the field
   * @param {number} to_index - The target index for the field
   */
  move_custom_form_field(from_index, to_index) {
    if (to_index >= this.custom_form_fields.length) {
      let k = to_index - this.custom_form_fields.length + 1;
      while (k--) {
        this.custom_form_fields.push(undefined);
      }
    }
    this.custom_form_fields.splice(
      to_index,
      0,
      this.custom_form_fields.splice(from_index, 1)[0]
    );
  }

  to_json() {
    return {
      name: this.name,
      description: this.description,
      legal_name: this.legal_name,
      support_email: this.support_email,
      messages_title: this.messages_title,
      messages_intro: this.messages_intro,
      event_reminder_emails_enabled: this.event_reminder_emails_enabled,
      announcement: this.announcement,
      announcement_enabled: this.announcement_enabled,
      logo_ref: this.logo_ref,
      app_icon_ref: this.app_icon_ref,
      email_signature_image_ref: this.email_signature_image_ref,
      email_signature_text: this.email_signature_text,
      home_page_preview_ref: this.home_page_preview_ref,
      terms_and_conditions_preview_ref: this.terms_and_conditions_preview_ref,
      privacy_policy_preview_ref: this.privacy_policy_preview_ref,
      grant_member_access_to_new_users: this.grant_member_access_to_new_users,
      signup_data_processing_consent_required: this.signup_data_processing_consent_required,

      // menu
      menu_placement: this.menu_placement,
      menu_track_start: this.menu_track_start,
      menu_track_end: this.menu_track_end,
      menu_background: this.menu_background,
      menu_elevation: this.menu_elevation,
      menu_mobile_alignment: this.menu_mobile_alignment,

      // control
      control_surface_align: this.control_surface_align,
      primary_control_surface: this.primary_control_surface,
      secondary_control_surface: this.secondary_control_surface,

      signup_or_login_text: this.signup_or_login_text,
      min_access_role_for_all_module: this.min_access_role_for_all_module,
      min_access_role_for_user_profiles: this.min_access_role_for_user_profiles,
      default_member_created_collection_entry_visibility: this.default_member_created_collection_entry_visibility,
      allow_member_collection_images: this.allow_member_collection_images,
      colors: this.colors.to_json(),
      typography: this.typography.to_json(),
      custom_css: this.custom_css,
      aspect_ratios: this.aspect_ratios,
      image_zoom: this.image_zoom,
      nav_items: this.nav_items.map(nav_item => nav_item.to_json()),
      footer_color: this.footer_color,
      footer_visible: this.footer_visible,
      footer_layout: this.footer_layout,
      footer_outro: this.footer_outro,
      footer_image_ref: this.footer_image_ref,

      footer_nav_1_title: this.footer_nav_1_title,
      footer_nav_1_description: this.footer_nav_1_description,
      footer_nav_1_show_in_mobile_drawer: this.footer_nav_1_show_in_mobile_drawer,
      footer_nav_1_items: this.footer_nav_1_items.map(nav_item => nav_item.to_json()),

      footer_nav_2_title: this.footer_nav_2_title,
      footer_nav_2_description: this.footer_nav_2_description,
      footer_nav_2_show_in_mobile_drawer: this.footer_nav_2_show_in_mobile_drawer,
      footer_nav_2_items: this.footer_nav_2_items.map(nav_item => nav_item.to_json()),

      footer_nav_3_title: this.footer_nav_3_title,
      footer_nav_3_description: this.footer_nav_3_description,
      footer_nav_3_show_in_mobile_drawer: this.footer_nav_3_show_in_mobile_drawer,
      footer_nav_3_items: this.footer_nav_3_items.map(nav_item => nav_item.to_json()),

      footer_nav_4_title: this.footer_nav_4_title,
      footer_nav_4_description: this.footer_nav_4_description,
      footer_nav_4_show_in_mobile_drawer: this.footer_nav_4_show_in_mobile_drawer,
      footer_nav_4_items: this.footer_nav_4_items.map(nav_item => nav_item.to_json()),

      footer_nav_5_title: this.footer_nav_5_title,
      footer_nav_5_description: this.footer_nav_5_description,
      footer_nav_5_show_in_mobile_drawer: this.footer_nav_5_show_in_mobile_drawer,
      footer_nav_5_items: this.footer_nav_5_items.map(nav_item => nav_item.to_json()),
      
      custom_form_fields: this.custom_form_fields.map(field => field.to_json()),

      // Webhook configuration
      webhook_enabled: this.webhook_enabled,
      webhook_url: this.webhook_url,
      webhook_events: this.webhook_events,
    };
  }

  toJSON() {
    return this.to_json();
  }

  __getNavItemModel(type) {
    let NavItemModel;
    if (type === 'entry') {
      NavItemModel = EntryNavItemModel;
    } else if (type === 'user_profile') {
      NavItemModel = UserProfileNavItemModel;
    } else if (type === 'messages') {
      NavItemModel = MessagesNavItemModel;
    } else if (type === 'calendar') {
      NavItemModel = CalendarNavItemModel;
    } else if (type === 'posters') {
      NavItemModel = PostersNavItemModel;
    } else if (type === 'link' || type === 'weblink') { // backwards compatibility for old type 'link'
      NavItemModel = WeblinkNavItemModel;
    } else if (type === 'phone') {
      NavItemModel = PhoneNavItemModel;
    } else if (type === 'email') {
      NavItemModel = EmailNavItemModel;
    } else if (type === 'spacer') {
      NavItemModel = SpacerNavItemModel;
    } else if (type === 'separator') {
      NavItemModel = SeparatorNavItemModel;
    } else if (type === 'mobile_menu_breakpoint') {
      NavItemModel = MobileMenuBreakpointNavItemModel;
    } else {
      throw new Error('Nav item must be of type entry|user_profile|messages|calendar|weblink|phone|email|spacer|separator|mobile_menu_breakpoint');
    }
    return NavItemModel
  }
}

export class TypographyModel {
  // Layout properties

  radius_min = $state(2);
  radius_max = $state(16);

  scale_base = $derived(Math.pow(this.radius_max / Math.max(this.radius_min, 1), 1/4)); // 4 steps
  
  // derived from min and max
  radius_sm = $derived(Math.round(this.radius_min * this.scale_base));
  radius_md = $derived(Math.round(this.radius_min * this.scale_base * this.scale_base));
  radius_lg = $derived(Math.round(this.radius_min * this.scale_base * this.scale_base * this.scale_base));

  input_border_radius = $state(26);

  page_inline_spacing_min = $state();
  page_inline_spacing_max = $state();
  grid_gutter = $state();
  block_spacing_multiplier = $state();
  
  animation_intensity = $state(); // value between 0 (no animations) and 1 (full animations). Default is 0.5 (balanced)
  
  /** Modular type scale with a phone and a desktop end, see typography_scale.js */
  scale = $state(normalize_scale());

  // Define a default typography style structure
  static DEFAULT_STYLE = {
    font_family: 'Inter', 
    type: 'variable',
    line_height: 1.5, 
    letter_spacing: 0,
    word_spacing: 0,
    letter_case: 'none', 
    size: { min: 16, max: 20 },
    weight: 400, 
    slant: 'normal', 
    axes: {
      italic: 0,
      slant: 0,
      weight: 400,
      width: 100,
      optical_sizing: 14,
    }
  };

  // Use the default typography style to initialize styles
  static DEFAULT_STYLES = {
    heading1: { ...TypographyModel.DEFAULT_STYLE },
    heading2: { ...TypographyModel.DEFAULT_STYLE },
    heading3: { ...TypographyModel.DEFAULT_STYLE },
    preamble: { ...TypographyModel.DEFAULT_STYLE },
    body:     { ...TypographyModel.DEFAULT_STYLE },
    control:  { ...TypographyModel.DEFAULT_STYLE },
    caption:  { ...TypographyModel.DEFAULT_STYLE },
  };

  styles = $state({...TypographyModel.DEFAULT_STYLES});

  font_specs = $state([])

  constructor({
    radius_min = 2,
    radius_max = 16,
    input_border_radius = 8,

    page_inline_spacing_min = '--size-4',
    page_inline_spacing_max = '--size-8',
    grid_gutter = '--size-3',
    block_spacing_multiplier = 1.41,
    animation_intensity = 0.5,

    styles,
    font_specs,
    scale,
    // Before the scale had two ends: one ratio ('custom' when none) and one base size
    typography_scale_ratio,
    typography_base_font_size,

  }  = {}) {

    this.font_specs = font_specs || [];

    this.radius_min = radius_min;
    this.radius_max = radius_max;
    this.input_border_radius = input_border_radius;

    this.page_inline_spacing_min = page_inline_spacing_min;
    this.page_inline_spacing_max = page_inline_spacing_max;
    this.grid_gutter = grid_gutter;
    this.block_spacing_multiplier = block_spacing_multiplier;
    this.animation_intensity = animation_intensity;
    this.scale = normalize_scale(scale, { typography_scale_ratio, typography_base_font_size });

    // Initialize or update typography styles with provided values or defaults
    Object.keys(this.styles).forEach(key => {
      // Get the provided style or an empty object if not provided
      let provided_style = styles?.[key] ?? {};
      // TODO: change to const again after all sites have updated

      // Backward compatibility: if 'controls' is provided, use it for 'control'
      // TODO: remove this in a future release, once all sites have updated
      if (key === 'control' && !provided_style.font_family && styles?.controls) {
        provided_style = styles.controls;
      }
      
      
      // Find the corresponding font family spec in the new structure
      const family_spec = Object.values(this.font_specs).find(
        (spec) => spec.family_name === provided_style.font_family
      );

      // Derive the type by checking if any font face in the family is variable
      const derived_type = family_spec?.font_faces?.some(
        (face) => face.font_file_type === 'variable'
      ) ? 'variable' : 'static';

      this.styles[key] = {
        ...TypographyModel.DEFAULT_STYLE,
        ...provided_style,
        type: derived_type,  // Use the derived type
        size: {
          ...TypographyModel.DEFAULT_STYLE.size,
          ...(provided_style.size ?? {})
        },
        axes: {
          ...TypographyModel.DEFAULT_STYLE.axes,
          ...(provided_style.axes ?? {})
        },
      };
    });
  }

  // We're calling this in layout.svelte
  generate_typography_variables(nav_items) {
    const css_vars = [];
    
    /* 
    REM BASE FOR FONT SIZE CALCULATIONS:
    
    We use 16px as the base for converting our pixel-based font sizes to rem units.
    This value represents the standard browser default root font-size.
    
    Why 16px specifically?
    1. Web standard: 16px is the default root font-size in all major browsers
    2. Accessibility: We don't override the root font-size in CSS, so users who 
       change their browser default (e.g., to 18px) will have that respected
    3. Predictable calculations: Our design tools work in pixels (e.g. Figma), 
       but CSS should use rem units for scalability
    
    Example: If we set body font to 18px:
    - Calculation: 18px ÷ 16px = 1.125rem
    - For user with 16px browser default: 1.125rem × 16px = 18px ✓
    - For user with 20px browser default: 1.125rem × 20px = 22.5px ✓ (scales appropriately)
    
    This creates a system where our designs are predictable but respect user preferences.
    
    Note: In our CSS, we have detailed guidelines about when to use rem vs em vs px vs other units.
    See the "WHEN TO USE WHICH CSS UNIT" section in typography.css for the complete guide.
    */
    const REM_BASE = 16;
    
    
    // Global border radi
    css_vars.push(`--radius-xs: ${this.radius_min}px;`);
    css_vars.push(`--radius-sm: ${this.radius_sm}px;`);
    css_vars.push(`--radius-md: ${this.radius_md}px;`);
    css_vars.push(`--radius-lg: ${this.radius_lg}px;`);
    css_vars.push(`--radius-xl: ${this.radius_max}px;`);
    css_vars.push(`--input-border-radius: var(--radius-lg);`);


    css_vars.push(`--page-inline-spacing-min: var(${this.page_inline_spacing_min});`);
    css_vars.push(`--page-inline-spacing-max: var(${this.page_inline_spacing_max});`);
    css_vars.push(`--grid-gutter: var(${this.grid_gutter});`);
    css_vars.push(`--block-spacing-multiplier: ${this.block_spacing_multiplier};`);
    

    // Calculate tallest nav item size
    const tallest_nav_item_size = Array.isArray(nav_items)
      ? nav_items.reduce((max, item) => {
          const size = item?.size ?? 20;
          return Math.max(max, size);
        }, 0)
      : 20; // Default in case of no nav_items

    css_vars.push(`--dynamic-island-max-nav-item-size: ${tallest_nav_item_size}px;`);

    css_vars.push(`--animation-intensity: ${this.animation_intensity};`);
    

    // Iterate over each style to generate its CSS variables
    Object.entries(this.styles).forEach(([style_name, style_props]) => {
      const { type, font_family, size, line_height, letter_spacing, word_spacing, weight, slant, letter_case, axes } = style_props;

      // Font family, then the generic family of its category (font_fallback.js)
      const family_spec = Object.values(this.font_specs).find((spec) => spec.family_name === font_family);
      css_vars.push(`--${style_name}-font-family: ${font_family_stack(font_family, family_spec)};`);

      // Font size with responsive scaling based on breakpoint
      // We no longer use fluid scaling, but a hard breakpoints at 769px and 1281px, 
      // due to performance issues when rendering text and doing layout calculations (becomes noticables during text editing).
      css_vars.push(
        [
          `--${style_name}-font-size: ${(size.min / REM_BASE).toFixed(4)}rem;`,
          `@media (min-width: 769px) { --${style_name}-font-size: ${((size.min + (size.max - size.min) / 2) / REM_BASE).toFixed(4)}rem; }`,
          `@media (min-width: 1281px) { --${style_name}-font-size: ${(size.max / REM_BASE).toFixed(4)}rem; }`
        ].join(' ')
      );

      // Line height (scale)
      css_vars.push(`--${style_name}-line-scale: ${line_height};`);

      // Letter spacing
      css_vars.push(`--${style_name}-letter-spacing: ${letter_spacing}em;`);

      // Word spacing
      if (word_spacing) {
        css_vars.push(`--${style_name}-word-spacing: ${word_spacing}em;`);
      }

      // letter case (text-transform)
      css_vars.push(`--${style_name}-letter-case: ${letter_case};`);
      
      // slant (font-style)
      css_vars.push(`--${style_name}-slant: ${slant};`);

      // Handle font weight and slant for static fonts
      if(type === 'static' && weight) {
        css_vars.push(`--${style_name}-font-weight: ${weight};`);
      }


      
      // Additional CSS variables for variable font axes as font-feature-settings:
      if (type === 'variable' && axes) {
        
        // Add separate CSS variables for specific axes
        if (type === 'variable' && axes) {
          if (axes.wght !== undefined) {
            css_vars.push(`--${style_name}-font-weight: ${axes.wght};`);
          }
          if (axes.opsz !== undefined) {
            css_vars.push(`--${style_name}-optical-sizing: ${axes.opsz};`);
          }
        }

        const variation_settings = [];
        const feature_settings = [];
  
        Object.entries(axes).forEach(([axis_name, axis_value]) => {
          if (axis_value !== undefined && axis_value !== 0) {
            const variation_prop = FONT_VARIATION_PROPERTIES.find(prop => prop.value === axis_name);
            const feature_prop = FONT_FEATURE_PROPERTIES.find(prop => prop.value === axis_name) || is_stylistic_set(axis_name);
      
            if (variation_prop) {
              variation_settings.push(`"${axis_name}" ${axis_value}`);
            } else if (feature_prop || is_stylistic_set(axis_name)) {
              feature_settings.push(`"${axis_name}" ${axis_value}`);
            }
            // If the axis is neither a variation nor a known feature or stylistic set, it will be ignored
          }
        });
  
        if (variation_settings.length > 0) {
          css_vars.push(`--${style_name}-font-variations: ${variation_settings.join(', ')};`);
        }
  
        if (feature_settings.length > 0) {
          css_vars.push(`--${style_name}-font-features: ${feature_settings.join(', ')};`);
        }
      }
  
    });

    return css_vars.join('\n');
  }

  // We're calling this in layout.svelte
  generate_font_faces() {
    return generate_font_faces_util(this.font_specs);
  }

  to_json() {
    return {
      radius_min: this.radius_min,
      radius_max: this.radius_max,
      input_border_radius: this.input_border_radius,
      page_inline_spacing_min: this.page_inline_spacing_min,
      page_inline_spacing_max: this.page_inline_spacing_max,
      grid_gutter: this.grid_gutter,
      block_spacing_multiplier: Number(this.block_spacing_multiplier),
      animation_intensity: Number(this.animation_intensity),
      styles: $state.snapshot(this.styles),
      // we do not save font_specs, because they are generated on the server dynamically from the default_assets folder
      scale: $state.snapshot(this.scale),
    };
  }

  toJSON() {
    return this.to_json();
  }
}
